import { createPreviz, linkPrevizToShot, type LibraryPrevizItem } from "@/lib/library-api"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"
import type { SceneStateSavePayload } from "@/lib/location-scene-3d/scene-state"

export type SavePrevizFromEngineInput = {
  engine: Scene3dEngine
  projectId: number
  locationId: number | null
  scene3dId?: number | null
  previzName: string
  shotId?: number | null
  getSceneState?: () => SceneStateSavePayload
  fromMs?: number
  toMs?: number
  /** false: не возвращать камеру в просмотр сцены после записи. */
  restoreView?: boolean
  /** После blob и возврата камеры, до загрузки файла. */
  onCaptured?: () => void
}

export type SavePrevizFromEngineResult =
  | { ok: true; previz: LibraryPrevizItem }
  | { ok: false; reason: "empty" | "no_path" }

/** Export path video and create a library previz. Shared by timeline Save. */
export async function savePrevizFromEngine(input: SavePrevizFromEngineInput): Promise<SavePrevizFromEngineResult> {
  if (!input.engine.hasSceneMotion()) return { ok: false, reason: "no_path" }

  const duration = input.engine.getSceneClockMs()
  const fromMs = input.fromMs ?? 0
  const toMs = input.toMs ?? duration
  const restoreView = input.restoreView !== false
  // Снимок до записи: уход со сцены подменяет engineRef, пока ролик ещё пишется.
  const sceneState = input.getSceneState?.()
  const releaseDetached = () => {
    if (input.engine.isExportDetached()) input.engine.destroy()
  }
  let blob: Blob
  try {
    blob = await input.engine.exportPathVideo({ fromMs, toMs, restoreView })
  } catch (error) {
    if (input.engine.isExportDetached()) releaseDetached()
    else if (!restoreView) input.engine.resize()
    throw error
  }
  if (blob.size === 0) {
    if (input.engine.isExportDetached()) releaseDetached()
    else if (!restoreView) input.engine.resize()
    return { ok: false, reason: "empty" }
  }

  if (restoreView && !input.engine.isExportDetached()) input.engine.animateToView("iso")

  const ext = blob.type.includes("mp4") ? "mp4" : "webm"
  const file = new File([blob], `previz.${ext}`, { type: blob.type || "video/webm" })
  const durationMs = toMs - fromMs
  input.onCaptured?.()

  try {
    let previz = await createPreviz(input.projectId, {
      file,
      name: input.previzName,
      locationId: input.locationId,
      scene3dId: input.scene3dId,
      durationMs,
      shotId: input.shotId,
      sceneState,
    })

    // Draft create never links the shot — attach on the final video save.
    if (input.shotId != null) {
      previz = await linkPrevizToShot(input.projectId, previz.id, input.shotId)
    }

    return { ok: true, previz }
  } finally {
    releaseDetached()
  }
}
